import { useMemo } from 'react'
import type { JSX } from 'react'

import {
  ChartSeriesProvider,
  ColumnChart,
  pickBucketGranularity,
} from '../charts'
import type { MetricSeries } from '../MetricChart/types'

import { toChartSeriesInputs } from './chartSeriesInputs'

interface PostsColumnChartProps {
  /** Built chart series for the Posts view: a count-per-day (Total) metric,
   * optionally followed by a dashed comparison sibling. */
  metrics: MetricSeries[]
  startDate?: Date
  endDate?: Date
  /** When true, the x-axis shows only the comparison-period dates. */
  comparisonOnly?: boolean
  height?: number
}

/**
 * Posts column chart: per-period post counts drawn as vertical columns. A thin
 * domain wrapper that maps the view's `MetricSeries` into the new charts module's
 * context and renders the generic {@link ColumnChart} — the count (Total) series
 * draws as columns and a comparison sibling as the muted grouped variant. Matches
 * how the Content Impact view draws its posts bars.
 *
 * @see ../charts/ColumnChart/ColumnChart
 */
export function PostsColumnChart({
  metrics,
  startDate,
  endDate,
  comparisonOnly = false,
  height,
}: PostsColumnChartProps): JSX.Element {
  // Bucket wider ranges (week/month) so posts columns stay readable instead of
  // collapsing to hairlines; the chart labels ticks/tooltip to match.
  const granularity =
    startDate && endDate ? pickBucketGranularity(startDate, endDate) : 'day'
  const series = useMemo(
    () => toChartSeriesInputs(metrics, granularity),
    [metrics, granularity],
  )

  return (
    <ChartSeriesProvider series={series}>
      <ColumnChart
        ariaLabel="Posts over time"
        startDate={startDate}
        endDate={endDate}
        comparisonOnly={comparisonOnly}
        monthly={granularity === 'month'}
        granularity={granularity}
        height={height}
      />
    </ChartSeriesProvider>
  )
}
